<script setup>
defineProps({
  icon: { type: [Object, Function], required: true },
  /** Accessible name, required because there is no visible text */
  label: { type: String, required: true },
  /** neutral (light grey fill, also accepted as outline), ink, ghost, orange */
  variant: { type: String, default: 'outline' },
  size: { type: String, default: 'md' },
  /** Shows a small orange dot, for unread states */
  dot: { type: Boolean, default: false },
  href: { type: String, default: null },
  disabled: { type: Boolean, default: false },
});
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    :href="href || undefined"
    :type="href ? undefined : 'button'"
    class="pd-iconbtn"
    :class="[`pd-iconbtn--${variant === 'outline' ? 'neutral' : variant}`, `pd-iconbtn--${size}`]"
    :aria-label="label"
    :disabled="href ? undefined : disabled"
    :aria-disabled="disabled || undefined"
  >
    <component :is="icon" :size="size === 'sm' ? 16 : 20" :stroke-width="2" aria-hidden="true" />
    <span v-if="dot" class="pd-iconbtn__dot" />
  </component>
</template>

<style scoped>
.pd-iconbtn {
  --d: 44px;
  position: relative;
  display: inline-grid;
  place-items: center;
  width: var(--d);
  height: var(--d);
  border-radius: 50%;
  border: 1.5px solid transparent;
  background: transparent;
  color: var(--pd-ink);
  cursor: pointer;
  transition: background-color var(--pd-fast) var(--pd-ease), transform var(--pd-fast) var(--pd-ease);
}
.pd-iconbtn:active { transform: scale(0.94); }
.pd-iconbtn--sm { --d: 34px; }
.pd-iconbtn--lg { --d: 52px; }
.pd-iconbtn--neutral { background: var(--pd-neutral); color: var(--pd-ink) !important; }
.pd-iconbtn--ink { background: var(--pd-ink); color: var(--pd-white); }
.pd-iconbtn--orange { background: var(--pd-orange); color: var(--pd-on-orange); }
@media (hover: hover) {
  .pd-iconbtn--neutral:hover { background: var(--pd-neutral-hover); }
  .pd-iconbtn--ghost:hover { background: var(--pd-neutral); }
}
.pd-iconbtn:disabled, .pd-iconbtn[aria-disabled='true'] { opacity: 0.4; cursor: not-allowed; transform: none; }
.pd-iconbtn__dot {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--pd-orange);
  box-shadow: 0 0 0 2px var(--pd-neutral); color: var(--pd-on-orange); }
</style>
